"use client";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { Calendar, MapPin } from "lucide-react";
import { useRef, useState } from "react";
import { getMediaUrl } from "../../lib/utils";

const getLocalizedText = (value, locale = "ar") => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return locale === "ar"
      ? value.ar || value.en || ""
      : value.en || value.ar || "";
  }
  return "";
};

const formatCampaignDate = (date, locale = "ar-IQ") => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const campaigns = [
  {
    title: "حملة شركة أسياسيل",
    date: "",
    image: "/2nd.png",
    location: "Baghdad, Iraq",
    description: [
      "تطوير حملة إعلانية متكاملة لإطلاق خدمات جديدة",
      "تصميم محتوى إبداعي متعدد المنصات",
      "تحقيق زيادة 45% في الوعي بالعلامة التجارية",
      "إدارة فريق من 8 أفراد لتنفيذ الحملة بنجاح",
    ],
  },
  {
    title: "حملة شركة زين العراق",
    date: "",
    image: "/acctern.png",
    location: "Baghdad, Iraq",
    description: [
      "إطلاق حملة تسويقية شاملة عبر وسائل التواصل الاجتماعي",
      "إنتاج محتوى فيديو احترافي للترويج للخدمات",
      "تحسين معدل التفاعل بنسبة 60%",
      "التعاون مع المؤثرين لتوسيع نطاق الوصول",
    ],
  },
  {
    title: "حملة مدينة الورد",
    date: "",
    image: "/DSC07858.jpg",
    location: "Baghdad, Iraq",
    description: [
      "تصميم هوية بصرية متكاملة للمشروع العقاري",
      "تنفيذ حملة تسويقية رقمية ومطبوعة",
      "زيادة الاستفسارات عن المشروع بنسبة 70%",
      "إدارة فعاليات الإطلاق وتغطيتها إعلامياً",
    ],
  },
  {
    title: "حملة الاتحاد البرلماني العربي",
    date: "",
    image: "/arabic1.jpg",
    location: "Baghdad, Iraq",
    description: [
      "تصميم وإنتاج مواد إعلامية للمؤتمر السنوي",
      "إدارة الحملة الرقمية عبر منصات التواصل",
      "تنسيق التغطية الإعلامية للفعاليات",
      "إنتاج تقارير مصورة احترافية للأحداث",
    ],
  },
  {
    title: "حملة كيت كات",
    date: "",
    image: "/kitcat1.jpg",
    location: "Baghdad, Iraq",
    description: [
      "تطوير حملة ترويجية مبتكرة للمنتج",
      "إنتاج محتوى إبداعي يستهدف الجمهور الشاب",
      "تحقيق نمو 50% في المبيعات خلال فترة الحملة",
      "تنفيذ استراتيجية تسويق رقمي متكاملة",
    ],
  },
  {
    title: "حملة شركة امواج الدولية",
    date: "18 Jan 2024",
    image: "/amuaj.jpg",
    location: "Baghdad, Iraq",
    description: [
      "إطلاق حملة علامة تجارية شاملة",
      "تطوير استراتيجية محتوى متعددة القنوات",
      "إدارة الإنتاج الإبداعي للمواد التسويقية",
      "تحقيق أهداف الحملة بنسبة تجاوزت 85%",
    ],
    current: true,
  },
];

const CampaignCard = ({ campaign, index, onSelect }) => {
  const cardRef = useRef(null);
  const isCardInView = useInView(cardRef, { once: true, margin: "-100px" });
  const formattedDate =
    formatCampaignDate(campaign.date, "ar-IQ") || campaign.date || "";
  const locationText = getLocalizedText(campaign.location, "ar");

  return (
    <motion.div
      ref={cardRef}
      className={`relative mb-8 md:mb-12 ${
        "md:flex md:items-center " +
        (index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse")
      }`}
      initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
      animate={isCardInView ? { opacity: 1, x: 0 } : {}}
      transition={{
        duration: 0.8,
        ease: "easeOut",
      }}
    >
      {/* Timeline Dot - Hidden on mobile */}
      <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-yellow-500 rounded-full border-4 border-white z-10 hidden md:block shadow-lg"></div>

      {/* Content Card */}
      <div
        className={`w-full md:w-5/12 ${
          index % 2 === 0 ? "md:pr-8" : "md:pl-8"
        }`}
      >
        <motion.div
          className="group cursor-pointer bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-200 relative"
          whileHover={{ scale: 1.02, y: -5 }}
          onClick={() => onSelect(campaign)}
        >
          {/* Current Badge */}
          {campaign.current && (
            <div className="absolute top-4 right-4 z-20">
              <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                Current
              </span>
            </div>
          )}

          {/* Image */}
          <div className="relative h-48 md:h-56 overflow-hidden">
            <img
              src={getMediaUrl((campaign.image?.url || campaign.image || "2nd.png").replace(/^\//, ""))}
              alt={campaign.title || "حملة"}
              loading={index < 2 ? "eager" : "lazy"}
              className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500"
              style={{ objectFit: "cover" }}
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-2">
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-1 group-hover:text-yellow-600 transition-colors">
                  {typeof campaign.title === "object" && campaign.title !== null
                    ? campaign.title.ar || campaign.title.en || "حملة"
                    : campaign.title || "حملة"}
                </h3>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center text-gray-500 text-xs md:text-sm mb-4 gap-2 sm:gap-4">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0 text-yellow-500" />
                <span className="text-xs md:text-sm">
                  {formattedDate || "—"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0 text-yellow-500" />
                <span className="text-xs md:text-sm break-words">
                  {locationText || "—"}
                </span>
              </div>
            </div>

            <ul className="space-y-2">
              {(Array.isArray(campaign.description)
                ? campaign.description
                : typeof campaign.description === "string"
                ? campaign.description
                    .split("\n")
                    .filter((line) => line.trim() !== "")
                : []
              ).map((item, idx) => (
                <li
                  key={idx}
                  className="text-gray-600 text-xs md:text-sm leading-relaxed flex items-start"
                >
                  <span className="text-yellow-500 mr-2 mt-1 flex-shrink-0">
                    •
                  </span>
                  <span className="break-words">{item}</span>
                </li>
              ))}
            </ul>

            {/* View Details */}
            <div className="mt-4 flex items-center text-yellow-600 font-semibold text-sm group-hover:text-yellow-700 transition-colors">
              <span className="ml-2">عرض التفاصيل</span>
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                ←
              </motion.span>
            </div>
          </div>

          {/* Floating Particles */}
          <motion.div
            className="absolute -top-2 -right-2 w-3 h-3 bg-yellow-500/60 rounded-full"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.6, 1, 0.6],
              rotate: [0, 180, 360],
            }}
            transition={{ duration: 3, repeat: Infinity, delay: index * 0.2 }}
          />
          <motion.div
            className="absolute -bottom-2 -left-2 w-2 h-2 bg-blue-900/60 rounded-full"
            animate={{
              scale: [1.5, 1, 1.5],
              opacity: [0.4, 0.8, 0.4],
              y: [0, -10, 0],
            }}
            transition={{ duration: 4, repeat: Infinity, delay: index * 0.3 }}
          />
        </motion.div>
      </div>

      {/* Spacer for the other side - Hidden on mobile */}
      <div className="hidden md:block md:w-5/12"></div>
    </motion.div>
  );
};

const CampaignsSection = ({ data }) => {
  // Helper function to normalize description (string or array) to array
  const normalizeDescription = (desc) => {
    if (!desc) return [];
    if (Array.isArray(desc)) return desc.filter(Boolean);
    if (typeof desc === "string") {
      // Split by newlines and filter out empty strings
      return desc.split("\n").filter((line) => line.trim() !== "");
    }
    return [];
  };

  // Helper to extract string from localized object or return string
  const getLocalizedValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      // If it's a localized object {en, ar}, use Arabic (ar) for Arabic page
      return value.ar || value.en || fallback;
    }
    return fallback;
  };

  // Fallback to hardcoded values if data is not available
  const title = getLocalizedValue(data?.title, "حملاتنا");
  const subtitle = getLocalizedValue(
    data?.subtitle,
    "أعمالنا المميزة التي نفتخر بها - حملات إعلانية حققت نتائج استثنائية"
  );
  const campaignsData =
    Array.isArray(data?.items) && data.items.length > 0
      ? data.items.map((item) => ({
          ...item,
          title: getLocalizedValue(item.title, item.title || ""),
          description: normalizeDescription(item.description),
        }))
      : campaigns;

  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const isInView = useInView(sectionRef, { once: true });

  return (
    <section
      ref={sectionRef}
      className="py-20 px-6 bg-gray-50/50 overflow-hidden"
    >
      <div className="container mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-yellow-500">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">{subtitle}</p>
        </motion.div>

        {/* Timeline */}
        <div ref={timelineRef} className="relative max-w-6xl mx-auto">
          {/* Timeline Line - Hidden on mobile */}
          <motion.div
            className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-yellow-500/30 h-full origin-top hidden md:block"
            initial={{ scaleY: 0 }}
            animate={isInView ? { scaleY: 1 } : {}}
            transition={{ duration: 2, ease: "easeOut" }}
          />

          {campaignsData.map((campaign, index) => (
            <CampaignCard
              key={index}
              campaign={campaign}
              index={index}
              onSelect={setSelectedCampaign}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedCampaign && (
          <CampaignModal
            campaign={selectedCampaign}
            onClose={() => setSelectedCampaign(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

const CampaignModal = ({ campaign, onClose }) => {
  console.log(campaign);
  const formattedDate =
    formatCampaignDate(campaign.date, "ar-IQ") || campaign.date || "";
  const locationText = getLocalizedText(campaign.location, "ar");
  return (
    <motion.div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-auto"
        initial={{ scale: 0.3, rotateY: -90, opacity: 0 }}
        animate={{ scale: 1, rotateY: 0, opacity: 1 }}
        exit={{ scale: 0.3, rotateY: 90, opacity: 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-64 md:h-96">
          <img
            src={getMediaUrl((campaign.image?.url || campaign.image || "2nd.png").replace(/^\//, ""))}
            alt={campaign.title}
            className="object-cover w-full h-full"
            style={{ objectFit: "cover" }}
          />
          <motion.button
            onClick={onClose}
            className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center hover:bg-white transition-colors duration-300 text-xl z-10"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            ✕
          </motion.button>
        </div>
        <motion.div
          className="p-6 md:p-8"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4 text-yellow-500" />
              <span>{formattedDate || "—"}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4 text-yellow-500" />
              <span>{locationText || "—"}</span>
            </div>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            {typeof campaign.title === "object" && campaign.title !== null
              ? campaign.title.ar || campaign.title.en || "حملة"
              : campaign.title || "حملة"}
          </h3>
          <ul className="space-y-3 mb-6">
            {(Array.isArray(campaign.description)
              ? campaign.description
              : typeof campaign.description === "string"
              ? campaign.description
                  .split("\n")
                  .filter((line) => line.trim() !== "")
              : []
            ).map((item, idx) => (
              <li
                key={idx}
                className="text-base md:text-lg text-gray-700 leading-relaxed flex items-start"
              >
                <span className="text-yellow-500 mr-2 mt-1 flex-shrink-0">
                  •
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed">
            تفاصيل الحملة الإعلانية وما تم تحقيقه من نجاحات ونتائج مميزة في هذا
            المشروع. هذه الحملة حققت نتائج استثنائية وساهمت في تعزيز الوعي
            بالعلامة التجارية وزيادة المبيعات بشكل ملحوظ.
          </p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default CampaignsSection;
